import type { ReactNode } from 'react';

import { useEffect, useState } from 'react';

import { getLogger } from '@/lib/logger/logger';

import { hcellService } from '../../features/hcell-sdk/services/hcellService';
import { RegionProvider } from '../../shared/contexts/RegionContext';
import { useRegion } from '../../shared/hooks/useRegion';
import { useAppStore } from '../../store';

const logger = getLogger('AppProviders');

interface AppProvidersProps {
  children: ReactNode;
}

// URL에서 SDK가 명시적으로 지정되어 있는지 확인
const hasExplicitSDKInURL = (): boolean => {
  const pathname = window.location.pathname;
  return (
    pathname.includes('/hcell-sdk') ||
    pathname.includes('/spreadsheet-sdk') ||
    pathname.includes('/docscompare-sdk') ||
    pathname.includes('/ocr-sdk') ||
    pathname.includes('/hwp-sdk') ||
    pathname.includes('/dataloader')
  );
};

// Inner component that has access to RegionContext
const AppProvidersInner = ({ children }: AppProvidersProps) => {
  const { initializeDefaultFeature, setCurrentSDK, currentSDK } = useAppStore();
  const { isLoading: isRegionLoading, isOverseas } = useRegion();
  const [samplesReady, setSamplesReady] = useState(false);

  // ✅ FIX: Wait for region detection to complete before initializing features
  // This ensures the correct language is loaded from the start
  useEffect(() => {
    // ✅ P0-1: Initialize HCell service on app mount
    void hcellService.init();
  }, []);

  // ✅ FIX: 지역 감지 완료 후 currentSDK를 지역에 맞게 동기화
  // URL에 명시적인 SDK가 없을 때만 적용 (기본 페이지 접속 시)
  useEffect(() => {
    if (!isRegionLoading && !hasExplicitSDKInURL()) {
      // 국내: hwp가 기본, 해외: hcell이 기본
      const defaultSDK = isOverseas ? 'hcell' : 'hwp';

      if (currentSDK !== defaultSDK) {
        logger.info(
          `[AppProviders] Syncing currentSDK with region: ${currentSDK} → ${defaultSDK} (isOverseas: ${isOverseas})`,
        );
        setCurrentSDK(defaultSDK);
      }
    }
  }, [isRegionLoading, isOverseas, currentSDK, setCurrentSDK]);

  useEffect(() => {
    if (!isRegionLoading) {
      logger.info('[AppProviders] Region detection complete, loading samples for current language');
      void (async () => {
        // 올바른 언어의 samples 로드 완료까지 대기 (해외: en/ja, 국내: ko)
        await hcellService.waitForCurrentLanguage();
        await initializeDefaultFeature();
        logger.info('[AppProviders] Default feature initialized');
        setSamplesReady(true);
      })();
    }
  }, [isRegionLoading, initializeDefaultFeature]);

  if (!samplesReady) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          fontSize: '16px',
          color: '#666',
        }}
      >
        Loading...
      </div>
    );
  }

  return <div className='app-providers'>{children}</div>;
};

export const AppProviders = ({ children }: AppProvidersProps) => {
  return (
    <RegionProvider enableTestMode={import.meta.env.DEV}>
      <AppProvidersInner>{children}</AppProvidersInner>
    </RegionProvider>
  );
};
